'use strict';

import qs from 'querystring';
import fetch from 'node-fetch';

import { BaseService } from '@narrate/base-sdk';

export default class MediaService extends BaseService {
  constructor() {
    super({ configPath: 'services.media' });
  }

  /**
   * Authenticated get for the owner of the set. 
   * Gets the media set and items with options.
   * 
   * @param {*} mediaSetId - set:nnnn-nnn-nnn
   * @param {*} options 
   *    -- fetchSize (optional) the number of images to get. Leave blank and for now default is 100
   *    -- lk - last item key for progressive fetch when used in combination with the fetchSize.
   *       The result will return an 'lk' which can be passed back into this for the next fetch.
   *       If none is returned, there is no more items to get in the progression.
   * @return the media set
   */
  async get(mediaSetId, options) {
    const { fetchSize, lk } = options;
    return await this.requestResource('getV2', {
      params: {
        mediaSetId,
        fetchSize,
        lk
      }
    });
  }

  /**
   * Save will create and save a new media set.
   * @param {object} mediaSetData 
   * @param {string} mediaSetId 
   */
  async save(mediaSetData, mediaSetId) {
    return await this.requestResource('saveV2', {
      method: 'post',
      data: mediaSetData,
      params: { mediaSetId },
    });
  }

  //TODO: [HQT] 10/30 remove permanently next iteration. Update is not used?
  // Update will only update the photoset main properties. It will no save/update the photo items/associations.
  // For an existing photoset - call savePhotosTo to add photos and save the photo items. Or call addPhotosTo to add existing photo to a set
  // async update(photoset, photosetId) {
  //   return await this.requestResource('update', {
  //     method: 'put',
  //     data: photoset,
  //     params: { photosetId }
  //   });
  // }

  /**
   * Save new photo items to an existing photo set. 
   * 
   * @param {*} photos -- array of new photo items. 
   * @param {*} medaSetId 
   */
  async savePhotosTo(photos, mediaSetId) {
    return await this.requestResource('savePhotosToV2', {
      method: 'post',
      data: photos,
      params: { mediaSetId }
    });
  }

  /**
   * Add existing photo items to an existing media set by passing an array of photo set Ids.
   * A set may contain videos and photos.
   * 
   * @param {*} photoIds  -- array of existing media ids where each media Id can be a photo or a video. 
   * @param {*} mediaSetId -- media set id to contain the media items
   */
  async addPhotosTo(photoIds, mediaSetId) {
    return await this.requestResource('addPhotosToV2', {
      method: 'put',
      data: photoIds,
      params: { mediaSetId }
    });
  }

  /**
   * Remove media items from a media set
   * In the case when the media item id is sent but is not in the set, this would still return success. 
   * @param {*} mediaItemIds -- array of item ids to remove
   * @param {*} mediaSetId -- the containing set
   */
  async removeItems(mediaItemIds, mediaSetId) {
    return await this.requestResource('removeItemsV2', {
      method: 'put',
      data: mediaItemIds,
      params: { mediaSetId }
    });
  }
  
  /**
   * Request to create a secure viewing link for the media set. 
   * 
   * The mediaSetId and the accessBlockId will be used to produce an entitlementId to be used with a security key to let
   * the users access the media set and images. 
   * 
   * @param {*} mediaSetId 
   * @param {*} accessBlockId 
   * @return {string} url string of the form: https://narrateapp.com/s/${mediaSetId}?&sk=1000000000000
   *   The link is to be used by the client viewing app which must pass both the mediaSetId and the sk to data API to get it's content to display
   * 
   */
  async createSecureLink(mediaSetId, accessBlockId) {
    return await this.requestResource('createSecureLinkV2', {
      method: 'post',
      data: {
        entitlementId: accessBlockId
      },
      params: { mediaSetId }, 
    });
  }

  /**
   * Draft/Experimental  method to create a new video asset. This will first generate an asset record, a new video Id, and
   * a signed video upload URL and return it. The app will upload the video and should call the updateVideo() to PUT any metadata to our
   * service to update our data. 
   * 
   * The options are experimental - they are not all being used, it is listed here varnames are self documnenting.. We will work out what makes 
   * sense to pass in as a create intent..
   * @param {object} options 
   *   mediaSetId - optional if we know it belongs to a set
   *   orientation - 'portrait' (default), 'landscape'
   *   width - not sure if we need yet or the video metadata will return it..
   *   height - like width
   *   quality - tbd, something like 720p, 1080p just adding it here for our consideration we need to solve/decide how we want this set.
   * @return {object} - An object containing the video id and the upload request tructure. The App can use the upload request url to upload via PUT
   *  { 
   *    videoId: the narrate video id to send back to update
   *    uploadRequest{}: upload request structure, use the url property to get the uploadlink. Not necessary to return this structure after uploading
   *  }
   */
  async newVideoUpload(options) {
    return await this.requestResource('newVideoUpload', {
      method: 'post',
      data: {
        options: {
          mediaSetId: options.mediaSetId,
          orientation: options.orientation,
          width: options.width,
          height: options.height,
          quality: options.quality
        }
      },
    });
  }

  /**
   * Draft/Experimental:
   * Once the app uploads the video and the user confirms. This should be called to save video metadata..
   * 
   * Note the final impl will need to figure out how to assign access block/media set and so forth.
   * 
   * @param {string} videoId - return the videoId 
   * @param {*} videoData 
   * @param {*} options 
   */
  async updateVideo(videoId, videoData, options) {
    return await this.requestResource('updateVideo', {
      method: 'put',
      data: {
        videoData,
        options
      }, 
      params: { videoId }
    });
  }

  /**
   * Draft/Experimental to pull set of videos for a user. We may later do it by the set Id. this is just for inital dev to trial the api
   * 
   * @param {string} userId -- temporary will not need after we turn on auth
   * @param {*} options -- supports progressive fetch with options: fetchSize, lk: { videoId }
   */
  async findVideos(userId, options) {
    return await this.requestResource('getVideos', {
      method: 'get',
      params: { userId },
      data: { options }
    });
  }
}
